import React, { useRef } from 'react';
import { connectInfiniteHits } from 'react-instantsearch-dom';

import { ActualPageProps } from '../../entities/Page';
import { ALL_DOCS, SIDEBAR_LANGUAGE, SIDEBAR_LIBRARY, noDatasheet, noPinout, noSchematics } from '../../utils/constants';
import { NoResultFound } from '../Icons';
import { LIMIT_FILES_HITS_PANEL, LIMIT_HARDWARE_HITS_PANEL, LIMIT_HITS_PANEL } from './SearchBar';
import SearchBarHit from './SearchBarHit';
import './SearchBarHits.scss';
import SearchBarResourceHit from './SearchBarResourceHit';

interface SearchBarHitsProps {
  hits: any;
  hasMore: boolean;
  saveRecentSearch: (val: any) => void;
  selectedPanelItem: number;
  setSelectedPanelSlug: (val: string) => void;
  actualPage: ActualPageProps;
  selectedPage: string;
  setHasResource: (val: string) => void;
  hasResource: string | null;
  setLimitHitsPanel: (val: any) => void;
  limitHitsPanel: number;
  setLimitHardwareHitsPanel: (val: any) => void;
  limitHardwareHitsPanel: number;
  setLimitFilesHitsPanel: (val: any) => void;
}

const SearchBarHits = ({
  hits,
  saveRecentSearch,
  selectedPanelItem,
  setSelectedPanelSlug,
  actualPage,
  selectedPage,
  setHasResource,
  hasResource,
  setLimitHitsPanel,
  limitHitsPanel,
  limitHardwareHitsPanel,
  setLimitHardwareHitsPanel,
  setLimitFilesHitsPanel,
}: SearchBarHitsProps): React.ReactElement => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isDocsContent = selectedPage !== SIDEBAR_LIBRARY && selectedPage !== SIDEBAR_LANGUAGE;

  // If no tutorials are found, the selected panel is set to empty
  let noTutorials = hits.filter((allHits) =>
    actualPage.title === selectedPage && selectedPage !== ALL_DOCS
      ? selectedPage === 'Hardware'
        ? allHits.slug?.split('/')[1] === actualPage.url.replace(/(^\/+|\/+$)/gm, '') || allHits.slug?.split('/')[1] === 'tutorials'
        : allHits.slug?.split('/')[1] === actualPage.url.replace(/(^\/+|\/+$)/gm, '')
      : allHits
  ).length;

  if (actualPage.title === selectedPage && selectedPage === SIDEBAR_LIBRARY) {
    noTutorials = hits.filter((allHits) => allHits.documentation_type === SIDEBAR_LIBRARY).length;
  }

  if (actualPage.title === selectedPage && selectedPage === SIDEBAR_LANGUAGE) {
    noTutorials = hits.filter(
      (allHits) => allHits.documentation_type === 'En' || allHits.documentation_type === 'De' || allHits.documentation_type === 'Pt'
    ).length;
  }

  // Show no results found if no tutorials are found
  if (noTutorials === 0) {
    setSelectedPanelSlug(null);
  }

  let filteredHits = hits;

  // Apply filter based on selectedPage
  if (selectedPage === 'Software') {
    filteredHits = filteredHits.filter((hit) => hit.category === 'software');
  } else if (selectedPage === 'Cloud') {
    filteredHits = filteredHits.filter((hit) => hit.category === 'arduino-cloud');
  } else if (selectedPage === 'Learn') {
    filteredHits = filteredHits.filter((hit) => hit.category === 'learn');
  }

  const searchBarHits = filteredHits.filter((hit) => hit.category !== 'hardware' && hit.documentation_type !== 'Resource').slice(0, LIMIT_HITS_PANEL);
  const hardwareHits = filteredHits.filter((hit) => hit.category === 'hardware').slice(0, LIMIT_HARDWARE_HITS_PANEL);
  const fileHits = filteredHits.filter((hit) => hit.documentation_type === 'Resource').slice(0, LIMIT_FILES_HITS_PANEL);

  setLimitHitsPanel(searchBarHits.length);
  setLimitHardwareHitsPanel(hardwareHits.length);
  setLimitFilesHitsPanel(fileHits.length);

  return (
    <>
      {isDocsContent &&
        searchBarHits
          .filter((allHits) =>
            actualPage.title === selectedPage && selectedPage !== ALL_DOCS
              ? selectedPage === 'Hardware'
                ? allHits.slug?.split('/')[1] === actualPage.url.replace(/(^\/+|\/+$)/gm, '') || allHits.slug?.split('/')[1] === 'tutorials'
                : selectedPage === 'Cloud'
                  ? allHits.slug?.split('/')[1] === actualPage.url.replace(/(^\/+|\/+$)/gm, '') || allHits.slug?.split('/')[1] === 'tutorials'
                  : allHits.slug?.split('/')[1] === actualPage.url.replace(/(^\/+|\/+$)/gm, '')
              : allHits
          )
          .filter((hwHit) => {
            if (hwHit.slug.split('/').includes('en') || hwHit.slug.split('/').includes('de') || hwHit.slug.split('/').includes('pt')) {
              if (hwHit.slug.split('/').includes(localStorage.getItem('gatsby-i18next-language'))) return true;
              else return false;
            }
            return true;
          })
          .map((hit, i) => (
            <SearchBarHit
              setSelectedPanelSlug={setSelectedPanelSlug}
              index={i}
              hit={hit}
              key={hit.slug}
              saveRecentSearch={saveRecentSearch}
              selectedPanelItem={selectedPanelItem}
              setHasResource={setHasResource}
              hasResource={hasResource}
            />
          ))}

      {isDocsContent && hardwareHits.length > 0 && <div className="search-hits__section">Hardware</div>}
      {isDocsContent &&
        hardwareHits.map((hit, i) => (
          <SearchBarHit
            setSelectedPanelSlug={setSelectedPanelSlug}
            index={limitHitsPanel + i}
            hit={hit}
            key={hit.slug}
            saveRecentSearch={saveRecentSearch}
            selectedPanelItem={selectedPanelItem}
            setHasResource={setHasResource}
            hasResource={hasResource}
          />
        ))}
      {isDocsContent && fileHits.length > 0 && <div className="search-hits__section">Files</div>}
      {isDocsContent &&
        fileHits.map((hit, i) => {
          const hasDatasheet = noDatasheet.includes(hit.title);
          const hasPinout = noPinout.includes(hit.title);
          const hasSchematics = noSchematics.includes(hit.title);
          return (
            <SearchBarResourceHit
              hit={hit}
              selectedPanelItem={selectedPanelItem}
              index={limitHitsPanel + limitHardwareHitsPanel + i}
              link={
                hit.pinout && !hasPinout
                  ? hit.pinout
                  : hit.datasheet && !hasDatasheet
                    ? hit.datasheet
                    : hit.schematic && !hasSchematics
                      ? hit.schematic
                      : null
              }
              name={
                hit.pinout && !hasPinout
                  ? 'Pinout'
                  : hit.datasheet && !hasDatasheet
                    ? 'Datasheet'
                    : hit.schematic && !hasSchematics
                      ? 'Schematic'
                      : null
              }
              key={hit.slug}
            />
          );
        })}

      {/* Library Searchbar */}
      {selectedPage === SIDEBAR_LIBRARY &&
        hits
          .filter((allHits) => allHits.documentation_type === SIDEBAR_LIBRARY)
          .map((hit, i) => {
            return i < LIMIT_HITS_PANEL ? (
              <SearchBarHit
                setSelectedPanelSlug={setSelectedPanelSlug}
                index={i}
                hit={hit}
                key={hit.slug}
                saveRecentSearch={saveRecentSearch}
                selectedPanelItem={selectedPanelItem}
                setHasResource={setHasResource}
                hasResource={hasResource}
              />
            ) : null;
          })}

      {/* Reference Searchbar */}
      {selectedPage === SIDEBAR_LANGUAGE &&
        hits
          .filter((allHits) => allHits.documentation_type === 'En' || allHits.documentation_type === 'De' || allHits.documentation_type === 'Pt')
          .filter((hwHit) => {
            if (hwHit.slug.split('/').includes('en') || hwHit.slug.split('/').includes('de') || hwHit.slug.split('/').includes('pt')) {
              if (hwHit.slug.split('/').includes(localStorage.getItem('gatsby-i18next-language'))) return true;
              else return false;
            }
            return true;
          })
          .map((hit, i) => {
            return i < LIMIT_HITS_PANEL ? (
              <SearchBarHit
                setSelectedPanelSlug={setSelectedPanelSlug}
                index={i}
                hit={hit}
                key={hit.slug}
                saveRecentSearch={saveRecentSearch}
                selectedPanelItem={selectedPanelItem}
                setHasResource={setHasResource}
                hasResource={hasResource}
              />
            ) : null;
          })}
      {(hits.length === 0 || noTutorials === 0) && (
        <span className="search-hits__no-tutorials">
          <NoResultFound />
          <div className="search-hits__no-tutorials--text">There are no results that match the search.</div>
        </span>
      )}
      {hits && <div style={{ width: '100%' }} ref={ref}></div>}
    </>
  );
};

export default connectInfiniteHits(SearchBarHits);
